import { Component, ElementRef, inject, signal, viewChild, OnInit, OnDestroy, NgZone, PLATFORM_ID, ChangeDetectionStrategy } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { AuthRoutingService, environment } from '@awo-care/shared-auth';

import { LanguageService } from '@awo-care/shared-core';
import { NavbarLanguageSelectorComponent } from './navbar-language-selector.component';
import { LogoWithBadgeComponent } from '@awo-care/shared-ui';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-navbar',
  imports: [
    MatButtonModule,
    MatIconModule,
    RouterModule,
    NavbarLanguageSelectorComponent,
    LogoWithBadgeComponent,
  ],
  template: `
    <nav class="navbar" role="navigation" aria-label="Hauptnavigation" i18n-aria-label="@@navbar.ariaLabel">
      <div class="navbar-container">
        <div class="navbar-brand">
          <a routerLink="/" class="brand-link" aria-label="AWO Care Startseite" i18n-aria-label="@@navbar.home.ariaLabel">
            <lib-logo-with-badge
              [src]="'shared-assets/icons/awo-care-logo.svg'"
              [alt]="'AWO Care Logo'"
              [logoClass]="'logo'"
              [tier]="environmentTier"
            />
          </a>
        </div>

        <div class="navbar-center">
          <ul class="nav-links" role="list">
            <li>
              <a
                [routerLink]="['/']"
                [fragment]="'how-it-works'"
                [class.active]="currentFragment() === 'how-it-works'"
                [attr.aria-current]="currentFragment() === 'how-it-works' ? 'page' : null"
                i18n="@@navbar.howItWorks"
              >
                Erste Schritte
              </a>
            </li>
            <li>
              <a
                [routerLink]="['/']"
                [fragment]="'schools'"
                [class.active]="currentFragment() === 'schools'"
                [attr.aria-current]="currentFragment() === 'schools' ? 'page' : null"
                i18n="@@navbar.schools"
              >
                Schulen
              </a>
            </li>
            <li>
              <a
                routerLink="/faq"
                routerLinkActive="active"
                i18n="@@navbar.faq"
              >
                Häufige Fragen
              </a>
            </li>
            <li>
              <a
                routerLink="/contact"
                routerLinkActive="active"
                i18n="@@navbar.help"
              >
                Hilfe
              </a>
            </li>
          </ul>
        </div>

        <div class="navbar-actions">
          <app-navbar-language-selector />
          <button mat-button class="login-button" (click)="navigateToLogin()" aria-label="Anmelden" i18n-aria-label="@@navbar.loginButton.ariaLabel">
            <mat-icon aria-hidden="true">person</mat-icon>
            <span i18n="@@navbar.login">Anmelden</span>
          </button>
          <button
            mat-flat-button
            color="primary"
            class="register-button"
            (click)="navigateToRegister()"
            aria-label="Registrieren"
            i18n-aria-label="@@navbar.registerButton.ariaLabel"
          >
            <span i18n="@@navbar.register">Registrieren</span>
          </button>
        </div>

        <!-- Fester Name, den Zustand sagt aria-expanded an („erweitert"/„reduziert"). -->
        <button
          #menuToggle
          mat-icon-button
          class="mobile-menu-toggle"
          (click)="toggleMobileMenu()"
          [attr.aria-expanded]="isMobileMenuOpen()"
          aria-label="Menü"
          i18n-aria-label="@@navbar.mobileMenu.toggle"
        >
          <mat-icon aria-hidden="true">{{ isMobileMenuOpen() ? 'close' : 'menu' }}</mat-icon>
        </button>
      </div>

      @if (isMobileMenuOpen()) {
      <div class="mobile-menu" role="region" aria-label="Mobile Navigation" i18n-aria-label="@@navbar.mobileMenu.region">
        <nav aria-label="Mobile Hauptnavigation" i18n-aria-label="@@navbar.mobileMenu.nav">
          <ul class="mobile-nav-links" role="list">
            <li>
              <a
                [routerLink]="['/']"
                [fragment]="'how-it-works'"
                [class.active]="currentFragment() === 'how-it-works'"
                [attr.aria-current]="currentFragment() === 'how-it-works' ? 'page' : null"
                (click)="closeMobileMenu()"
                i18n="@@navbar.howItWorks"
              >
                Erste Schritte
              </a>
            </li>
            <li>
              <a
                [routerLink]="['/']"
                [fragment]="'schools'"
                [class.active]="currentFragment() === 'schools'"
                [attr.aria-current]="currentFragment() === 'schools' ? 'page' : null"
                (click)="closeMobileMenu()"
                i18n="@@navbar.schools"
              >
                Schulen
              </a>
            </li>
            <li>
              <a
                routerLink="/faq"
                routerLinkActive="active"
                (click)="closeMobileMenu()"
                i18n="@@navbar.faq"
              >
                Häufige Fragen
              </a>
            </li>
            <li>
              <a
                routerLink="/contact"
                routerLinkActive="active"
                (click)="closeMobileMenu()"
                i18n="@@navbar.help"
              >
                Hilfe
              </a>
            </li>
          </ul>
        </nav>
        <div class="mobile-actions">
          <div class="mobile-language-selector">
            <app-navbar-language-selector />
          </div>
          <button
            mat-stroked-button
            class="mobile-login-button"
            (click)="navigateToLogin()"
            aria-label="Anmelden"
            i18n-aria-label="@@navbar.loginButton.ariaLabel"
          >
            <mat-icon aria-hidden="true">person</mat-icon>
            <span i18n="@@navbar.login">Anmelden</span>
          </button>
          <button
            mat-flat-button
            color="primary"
            class="mobile-register-button"
            (click)="navigateToRegister()"
            aria-label="Registrieren"
            i18n-aria-label="@@navbar.registerButton.ariaLabel"
          >
            <span i18n="@@navbar.register">Registrieren</span>
          </button>
        </div>
      </div>
      }
    </nav>
  `,
  host: { '(document:keydown.escape)': 'onEscape()' },
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 1000;
      }

      .navbar {
        background: white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      }

      .navbar-container {
        padding: 0 1rem;
        height: 72px;
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        gap: 2rem;
      }

      .navbar-brand {
        justify-self: start;

        .brand-link {
          display: flex;
          align-items: center;
          text-decoration: none;
        }

        .logo {
          height: 40px;
          width: auto;
          transition: opacity 0.2s ease;

          &:hover {
            opacity: 0.8;
          }
        }
      }

      .navbar-center {
        justify-self: center;

        .nav-links {
          display: flex;
          list-style: none;
          margin: 0;
          padding: 0;
          gap: 2.5rem;

          li {
            a {
              text-decoration: none;
              color: var(--awo-text-muted, #6f6f6f);
              font-size: 0.95rem;
              font-weight: 400;
              transition: color 0.2s ease;
              position: relative;
              padding: 0.5rem 0;
              white-space: nowrap;
              cursor: pointer;

              &:hover {
                color: var(--theme-primary);
              }

              &.active {
                color: var(--theme-primary);

                &::after {
                  content: '';
                  position: absolute;
                  bottom: -4px;
                  left: 0;
                  right: 0;
                  height: 2px;
                  background: var(--theme-primary);
                }
              }
            }
          }
        }
      }

      .navbar-actions {
        justify-self: end;
        display: flex;
        gap: 1rem;
        align-items: center;

        .login-button {
          color: #666;
          height: 40px;
          padding: 0 1rem;
          font-size: 0.95rem;
          font-weight: 500;
          border-radius: 20px;
          transition: all 0.3s ease;

          mat-icon {
            margin-right: 0.25rem;
            font-size: 18px;
            width: 18px;
            height: 18px;
          }

          &:hover {
            color: var(--theme-primary);
            transform: translateY(-2px);
          }
        }

        .register-button {
          border-radius: 20px;
          padding: 0 1.5rem;
          height: 40px;
          font-weight: 500;
          font-size: 0.95rem;
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(226, 56, 42, 0.3);
          }
        }
      }

      .mobile-menu-toggle {
        display: none;
      }

      .mobile-menu {
        display: none;
        background: white;
        border-top: 1px solid #eee;
        padding: 1.5rem;

        .mobile-nav-links {
          list-style: none;
          margin: 0 0 1.5rem 0;
          padding: 0;

          li {
            a {
              display: block;
              padding: 0.75rem 1rem;
              text-decoration: none;
              color: #666;
              font-size: 1.1rem;
              font-weight: 500;
              transition: all 0.2s ease;
              border-radius: 8px;
              cursor: pointer;

              &:hover {
                background: #f5f5f5;
                color: var(--theme-primary);
              }

              &.active {
                background: rgba(var(--theme-primary-rgb), 0.1);
                color: var(--theme-primary);
              }
            }
          }
        }

        .mobile-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;

          .mobile-language-selector {
            display: flex;
            justify-content: center;
            padding: 0.5rem 0;
          }

          button {
            width: 100%;
            height: 56px;
            font-size: 1.1rem;
            font-weight: 500;
            border-radius: 28px;
            padding: 0 2rem;
            transition: all 0.3s ease;

            mat-icon {
              margin-right: 0.5rem;
            }

            &:hover {
              transform: translateY(-3px);
            }
          }

          .mobile-register-button:hover {
            box-shadow: 0 15px 30px rgba(226, 56, 42, 0.3);
          }
        }
      }

      @media (max-width: 768px) {
        .navbar-container {
          height: 64px;
          grid-template-columns: 1fr auto;
        }

        .navbar-center,
        .navbar-actions {
          display: none;
        }

        .mobile-menu-toggle {
          display: block;
          justify-self: end;
        }

        .mobile-menu {
          display: block;
        }

        .navbar-brand .logo {
          height: 32px;
        }
      }
    `,
  ],
})
export class NavbarComponent implements OnInit, OnDestroy {
  private authRoutingService = inject(AuthRoutingService);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private ngZone = inject(NgZone);
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  isMobileMenuOpen = signal(false);
  private menuToggle = viewChild('menuToggle', { read: ElementRef<HTMLButtonElement> });
  currentFragment = signal<string | null>(null);
  environmentTier = environment.tier;
  private routerSubscription?: Subscription;
  private observer?: IntersectionObserver;
  private mutationObserver?: MutationObserver;
  private readonly sectionIds = ['how-it-works', 'schools'];
  private visibleSections = new Map<string, number>();

  ngOnInit() {
    this.currentFragment.set(this.router.parseUrl(this.router.url).fragment);

    this.routerSubscription = this.router.events.subscribe(() => {
      const url = this.router.parseUrl(this.router.url);
      if (url.root.children['primary']?.segments.length) {
        // Navigated away from home — clear highlight and stop observing
        this.currentFragment.set(null);
        this.teardownScrollSpy();
      } else {
        // On home route — start scroll spy
        if (this.isBrowser) {
          this.setupScrollSpy();
        }
      }
    });

    // Initial setup if already on home
    if (this.isBrowser && !this.router.parseUrl(this.router.url).root.children['primary']?.segments.length) {
      this.setupScrollSpy();
    }
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.teardownScrollSpy();
  }

  private setupScrollSpy() {
    this.teardownScrollSpy();
    this.visibleSections.clear();

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.visibleSections.set(entry.target.id, entry.intersectionRatio);
          } else {
            this.visibleSections.delete(entry.target.id);
          }
        }

        let bestId: string | null = null;
        let bestRatio = 0;
        for (const [id, ratio] of this.visibleSections) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id;
          }
        }

        this.ngZone.run(() => this.currentFragment.set(bestId));
      },
      {
        rootMargin: '-72px 0px 0px 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
      },
    );

    // Observe existing sections
    this.observeSections();

    // Watch for new sections appearing (e.g. @defer blocks)
    this.mutationObserver = new MutationObserver(() => this.observeSections());
    this.mutationObserver.observe(this.document.body, { childList: true, subtree: true });
  }

  private observeSections() {
    if (!this.observer) return;
    for (const id of this.sectionIds) {
      const el = this.document.getElementById(id);
      if (el) {
        this.observer.observe(el);
      }
    }
  }

  private teardownScrollSpy() {
    this.observer?.disconnect();
    this.observer = undefined;
    this.mutationObserver?.disconnect();
    this.mutationObserver = undefined;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen.update((open) => !open);
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }

  /** Escape schließt das mobile Menü und gibt den Fokus an den Menü-Button zurück. */
  onEscape() {
    if (!this.isMobileMenuOpen()) return;
    this.closeMobileMenu();
    this.menuToggle()?.nativeElement.focus();
  }

  navigateToLogin() {
    const lang = this.languageService.currentLanguage().code;
    this.document.location.href = `${this.authRoutingService.getPortalUrl()}/${lang}/auth/login`;
  }

  navigateToRegister() {
    const lang = this.languageService.currentLanguage().code;
    this.document.location.href = `${this.authRoutingService.getPortalUrl()}/${lang}/auth/register`;
  }
}
